/**
 * 🆕 [2026-06-11 v4] Utilitaires frontend pour les heures d'ouverture des fournisseurs/magasins.
 *
 * Format des heures : `HH:MM` (24h) — ex: "07:30", "18:00".
 * Structure attendue :
 *   {
 *     lundi:    { ouvert: true,  ouverture: "06:00", fermeture: "17:00" },
 *     mardi:    { ouvert: true,  ouverture: "06:00", fermeture: "17:00" },
 *     ...
 *     dimanche: { ouvert: false, ouverture: null,    fermeture: null    },
 *   }
 */

export const JOURS_SEMAINE = [
  { key: 'lundi',    label: 'Lundi',    abbr: 'Lun' },
  { key: 'mardi',    label: 'Mardi',    abbr: 'Mar' },
  { key: 'mercredi', label: 'Mercredi', abbr: 'Mer' },
  { key: 'jeudi',    label: 'Jeudi',    abbr: 'Jeu' },
  { key: 'vendredi', label: 'Vendredi', abbr: 'Ven' },
  { key: 'samedi',   label: 'Samedi',   abbr: 'Sam' },
  { key: 'dimanche', label: 'Dimanche', abbr: 'Dim' },
];

/** Heures par défaut quand on active la section : Lun-Ven ouvert, Sam-Dim fermé. */
export function heuresParDefaut() {
  const base = (ouvert) => ({ ouvert, ouverture: ouvert ? '07:00' : null, fermeture: ouvert ? '17:00' : null });
  return {
    lundi: base(true), mardi: base(true), mercredi: base(true),
    jeudi: base(true), vendredi: base(true),
    samedi: base(false), dimanche: base(false),
  };
}

/** Format "07:00" → "7h00", "07:30" → "7h30", "18:00" → "18h00". */
export function formatHeureFR(hhmm) {
  if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return '';
  const [h, m] = hhmm.split(':');
  return `${parseInt(h, 10)}h${m}`;
}

/** Retourne l'heure (HH:MM) à Toronto/Québec sous forme {jour: 'lundi', minutes: 870}. */
export function maintenantQuebec(now = new Date()) {
  // Convertit la date courante en string ISO dans le fuseau Toronto via toLocaleString
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Toronto',
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(now);
  const weekday = (parts.find(p => p.type === 'weekday') || {}).value || '';
  const hour = parseInt((parts.find(p => p.type === 'hour') || {}).value || '0', 10);
  const minute = parseInt((parts.find(p => p.type === 'minute') || {}).value || '0', 10);
  const map = {
    monday: 'lundi', tuesday: 'mardi', wednesday: 'mercredi', thursday: 'jeudi',
    friday: 'vendredi', saturday: 'samedi', sunday: 'dimanche',
  };
  return { jour: map[weekday.toLowerCase()] || 'lundi', minutes: hour * 60 + minute };
}

function toMinutes(hhmm) {
  if (!hhmm || !hhmm.includes(':')) return null;
  const [h, m] = hhmm.split(':');
  return parseInt(h, 10) * 60 + parseInt(m, 10);
}

/**
 * Calcule l'état courant : `{ disponible, ouvert, label, badge_color, ferme_dans_minutes }`.
 *
 * - `disponible: false` si heures non renseignées.
 * - `ouvert: true|false`.
 * - `label`: texte FR-CA pour l'utilisateur ("Ouvert jusqu'à 17h00", "Ferme dans 30 min", "Fermé · Ouvre demain à 6h30").
 * - `badge_color`: 'green' (ouvert) | 'amber' (ferme bientôt <1h) | 'red' (fermé).
 */
export function calculerStatutOuverture(heures, now = new Date()) {
  if (!heures || typeof heures !== 'object') {
    return { disponible: false, ouvert: false, label: 'Heures non renseignées', badge_color: 'gray' };
  }
  const { jour, minutes } = maintenantQuebec(now);
  const jourData = heures[jour] || {};

  if (!jourData.ouvert) {
    // Cherche le prochain jour ouvert
    const idx = JOURS_SEMAINE.findIndex(j => j.key === jour);
    for (let i = 1; i <= 7; i++) {
      const next = JOURS_SEMAINE[(idx + i) % 7];
      const d = heures[next.key];
      if (d && d.ouvert && d.ouverture) {
        const labelJour = i === 1 ? 'demain' : next.label.toLowerCase();
        return {
          disponible: true,
          ouvert: false,
          label: `Fermé · Ouvre ${labelJour} à ${formatHeureFR(d.ouverture)}`,
          badge_color: 'red',
        };
      }
    }
    return { disponible: true, ouvert: false, label: 'Fermé', badge_color: 'red' };
  }

  const open = toMinutes(jourData.ouverture);
  const close = toMinutes(jourData.fermeture);
  if (open === null || close === null) {
    return { disponible: true, ouvert: false, label: 'Fermé', badge_color: 'red' };
  }

  if (minutes < open) {
    return {
      disponible: true,
      ouvert: false,
      label: `Ferme · Ouvre aujourd'hui à ${formatHeureFR(jourData.ouverture)}`,
      badge_color: 'red',
    };
  }
  if (minutes >= close) {
    // Cherche le prochain jour ouvert
    const idx = JOURS_SEMAINE.findIndex(j => j.key === jour);
    for (let i = 1; i <= 7; i++) {
      const next = JOURS_SEMAINE[(idx + i) % 7];
      const d = heures[next.key];
      if (d && d.ouvert && d.ouverture) {
        const labelJour = i === 1 ? 'demain' : next.label.toLowerCase();
        return {
          disponible: true,
          ouvert: false,
          label: `Fermé · Ouvre ${labelJour} à ${formatHeureFR(d.ouverture)}`,
          badge_color: 'red',
        };
      }
    }
    return { disponible: true, ouvert: false, label: 'Fermé', badge_color: 'red' };
  }

  // Actuellement ouvert
  const restant = close - minutes;
  if (restant <= 60) {
    return {
      disponible: true,
      ouvert: true,
      label: `Ferme dans ${restant} min (à ${formatHeureFR(jourData.fermeture)})`,
      badge_color: 'amber',
      ferme_dans_minutes: restant,
    };
  }
  return {
    disponible: true,
    ouvert: true,
    label: `Ouvert jusqu'à ${formatHeureFR(jourData.fermeture)}`,
    badge_color: 'green',
    ferme_dans_minutes: restant,
  };
}

/**
 * Regroupe les jours qui ont les mêmes heures (Lun-Ven 6h30-18h00 / Sam 8h-17h / Dim fermé).
 * Retourne un tableau de `{ label: 'Lun-Ven', heures: '6h30-18h00' }` ou `{ label: 'Dim', heures: 'Fermé' }`.
 */
export function regrouperHeures(heures) {
  if (!heures) return [];
  const lignes = JOURS_SEMAINE.map(j => {
    const d = heures[j.key] || {};
    const txt = (!d.ouvert || !d.ouverture || !d.fermeture)
      ? 'Fermé'
      : `${formatHeureFR(d.ouverture)}-${formatHeureFR(d.fermeture)}`;
    return { abbr: j.abbr, txt };
  });

  const groupes = [];
  let cur = null;
  for (const l of lignes) {
    if (cur && cur.txt === l.txt) {
      cur.abbrs.push(l.abbr);
    } else {
      if (cur) groupes.push(cur);
      cur = { txt: l.txt, abbrs: [l.abbr] };
    }
  }
  if (cur) groupes.push(cur);

  return groupes.map(g => ({
    label: g.abbrs.length === 1 ? g.abbrs[0] : `${g.abbrs[0]}-${g.abbrs[g.abbrs.length - 1]}`,
    heures: g.txt,
  }));
}
